import type { createDurabilityCoordinator } from '../durability/durability.ts';
import type { SmtP01WorkspaceCheckpoint, SmtP01PersistedWorkspaceState } from './smt-p01-workspace-contract.ts';

type DurabilityCoordinator = ReturnType<typeof createDurabilityCoordinator>;

const SMT_P01_WORKSPACE_SCHEMA_VERSION = 2;
const LEGACY_SMT_P01_WORKSPACE_SCHEMA_VERSION = 1;
const keyFor = (storeId: string): string => `smt-p01-workspace:store:${storeId}`;
const clone = <T>(value: T): T => structuredClone(value);

interface SmtP01WorkspacePayload {
  readonly storeId: string;
  readonly items: readonly SmtP01PersistedWorkspaceState[];
}

interface LegacySmtP01WorkspacePayload {
  readonly storeId: string;
  readonly items: readonly unknown[];
}

const assertText = (value: string, code: string): string => {
  if (!value?.trim()) throw new Error(code);
  return value.trim();
};

const assertBusinessDate = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('ACTIVE_WORK_BUSINESS_DATE_INVALID');
  return value;
};

const assertItems = (items: readonly SmtP01PersistedWorkspaceState[]): void => {
  const ids = items.map((item) => assertText(item.workspaceId, 'ACTIVE_WORK_WORKSPACE_ID_REQUIRED'));
  if (new Set(ids).size !== ids.length) throw new Error('ACTIVE_WORK_WORKSPACE_ID_DUPLICATE');
  for (const item of items) {
    if (item.serviceMode !== 'dine-in' && item.serviceMode !== 'takeaway') {
      throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_INVALID');
    }
    if (!Array.isArray(item.lineServiceModes)) throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_INVALID');
  }
};

const isSchemaUnsupported = (error: unknown, version: number): boolean =>
  error instanceof Error && error.message === `DURABILITY_SCHEMA_UNSUPPORTED:${version}`;

export function createSmtP01WorkspaceDurability(
  coordinator: Pick<DurabilityCoordinator, 'commit' | 'restore'>,
) {
  return {
    async load(storeId: string): Promise<SmtP01WorkspaceCheckpoint | null> {
      const acceptedStoreId = assertText(storeId, 'ACTIVE_WORK_STORE_ID_REQUIRED');
      const key = keyFor(acceptedStoreId);
      let checkpoint;
      try {
        checkpoint = await coordinator.restore<SmtP01WorkspacePayload>(
          key,
          SMT_P01_WORKSPACE_SCHEMA_VERSION,
        );
      } catch (error) {
        if (!isSchemaUnsupported(error, LEGACY_SMT_P01_WORKSPACE_SCHEMA_VERSION)) throw error;
        const legacy = await coordinator.restore<LegacySmtP01WorkspacePayload>(
          key,
          LEGACY_SMT_P01_WORKSPACE_SCHEMA_VERSION,
        );
        if (!legacy) return null;
        if (legacy.payload.storeId !== acceptedStoreId) throw new Error('ACTIVE_WORK_STORE_ID_MISMATCH');
        if (!Array.isArray(legacy.payload.items)) throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_MIGRATION_REQUIRED');
        if (legacy.payload.items.length > 0) throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_MIGRATION_REQUIRED');
        return clone({
          revision: legacy.revision,
          businessDate: legacy.businessDate,
          items: [],
        });
      }
      if (!checkpoint) return null;
      if (checkpoint.payload.storeId !== acceptedStoreId) throw new Error('ACTIVE_WORK_STORE_ID_MISMATCH');
      assertItems(checkpoint.payload.items);
      return clone({
        revision: checkpoint.revision,
        businessDate: checkpoint.businessDate,
        items: checkpoint.payload.items,
      });
    },

    async save(input: {
      readonly storeId: string;
      readonly businessDate: string;
      readonly items: readonly SmtP01PersistedWorkspaceState[];
      readonly expectedRevision: number;
      readonly committedAt: string;
    }): Promise<SmtP01WorkspaceCheckpoint> {
      const storeId = assertText(input.storeId, 'ACTIVE_WORK_STORE_ID_REQUIRED');
      const businessDate = assertBusinessDate(input.businessDate);
      assertItems(input.items);
      const checkpoint = await coordinator.commit<SmtP01WorkspacePayload>({
        key: keyFor(storeId),
        schemaVersion: SMT_P01_WORKSPACE_SCHEMA_VERSION,
        businessDate,
        payload: { storeId, items: clone(input.items) },
        committedAt: input.committedAt,
      }, input.expectedRevision);
      return clone({
        revision: checkpoint.revision,
        businessDate: checkpoint.businessDate,
        items: checkpoint.payload.items,
      });
    },
  };
}
